import { buildServiceCategoryHref } from '@/utilities/buildServiceCategoryHref';

import { CAR_BATTERY_VEHICLE_TYPE_ID, MOTOR_BATTERY_VEHICLE_TYPE_ID } from '@/constants/batteryProduct';
import { extraCategoryMegaMenuItems } from '@/constants/categoryMegaMenu';
import { servicePage } from '@/constants/routes';

import { CategoryTreeNode } from '@/server/services/core/category/models';
import { CategoryMegaMenuItem, CategoryMegaMenuLink, CategoryMegaMenuSubCategory } from '@/types/categoryMegaMenu';

type ChildrenByParentId = Map<number | null, CategoryTreeNode[]>;

const buildRootCategoryHref = (slug: string) => `${servicePage}/${slug}`;

const buildSubCategoryHref = (rootSlug: string, categorySlug: string) => buildServiceCategoryHref(rootSlug, categorySlug);

const groupCategoriesByParentId = (categories: CategoryTreeNode[]): ChildrenByParentId => {
    const childrenByParentId: ChildrenByParentId = new Map();

    categories.forEach((category) => {
        const siblings = childrenByParentId.get(category.parentId) ?? [];
        siblings.push(category);
        childrenByParentId.set(category.parentId, siblings);
    });

    return childrenByParentId;
};

const getRootCategories = (categories: CategoryTreeNode[], childrenByParentId: ChildrenByParentId) =>
    childrenByParentId.get(null) ?? categories.filter((category) => category.parentId === null);

const getFlatDescendants = (parentId: number, childrenByParentId: ChildrenByParentId): CategoryTreeNode[] => {
    const descendants: CategoryTreeNode[] = [];
    const queue = [...(childrenByParentId.get(parentId) ?? [])];

    while (queue.length) {
        const category = queue.shift()!;

        descendants.push(category);
        queue.push(...(childrenByParentId.get(category.id) ?? []));
    }

    return descendants;
};

const mapCategoryToLink = (category: CategoryTreeNode, rootSlug: string, isRoot = false): CategoryMegaMenuLink => ({
    id: category.id,
    name: category.name,
    slug: category.slug,
    href: isRoot ? buildRootCategoryHref(category.slug) : buildSubCategoryHref(rootSlug, category.slug)
});

const mapToSubCategory = (category: CategoryTreeNode, rootSlug: string, childrenByParentId: ChildrenByParentId): CategoryMegaMenuSubCategory => ({
    ...mapCategoryToLink(category, rootSlug),
    children: getFlatDescendants(category.id, childrenByParentId).map((descendant) => mapCategoryToLink(descendant, rootSlug))
});

const getHrefSlug = (href: string) => href.replace(/^\/+|\/+$/g, '') || href;

const mapExtraCategoryMegaMenuItems = (): CategoryMegaMenuItem[] =>
    extraCategoryMegaMenuItems.map((item, index) => ({
        id: -(index + 1),
        name: item.name,
        slug: getHrefSlug(item.href),
        href: item.href,
        subCategories: item.children.map((child, childIndex) => ({
            id: -((index + 1) * 100 + childIndex + 1),
            name: child.name,
            slug: getHrefSlug(child.href),
            href: child.href,
            categoryId: child.categoryId,
            children: []
        }))
    }));

export const buildCategoryMegaMenuItems = (categories: CategoryTreeNode[]): CategoryMegaMenuItem[] => {
    const menuCategories = categories.filter(
        (category) => category.slug !== CAR_BATTERY_VEHICLE_TYPE_ID && category.slug !== MOTOR_BATTERY_VEHICLE_TYPE_ID
    );
    const childrenByParentId = groupCategoriesByParentId(menuCategories);

    const apiCategoryMegaMenuItems = getRootCategories(menuCategories, childrenByParentId).map((rootCategory) => ({
        ...mapCategoryToLink(rootCategory, rootCategory.slug, true),
        subCategories: (childrenByParentId.get(rootCategory.id) ?? []).map((levelOneCategory) =>
            mapToSubCategory(levelOneCategory, rootCategory.slug, childrenByParentId)
        )
    }));

    return [...mapExtraCategoryMegaMenuItems(), ...apiCategoryMegaMenuItems];
};
